// filepath: apps/web/src/admin/formula/useFormulaValidate.ts
//
// FR-UI-ADM-3 / FR-VAL-4 — debounced + AbortController-wrapped hook
// for the server-side formula validator.
//
// Wire contract (verified at internal/formula/handler.go:ServeValidate):
//   POST /api/formula/validate
//     body:    { formula: string, context?: map<string,any> }
//     reply:   { ok: bool, type?: string, error?: string }
//     status:  200 always (success OR compile-error both 200);
//              400 only on malformed JSON or empty formula.
//   NO `fieldPath`, NO `locale` in the wire body — these are
//   metadata that the SPA attaches for cache-key grouping + UX
//   mapping but never round-trip to the server. The 9 ACs in
//   .copilot/tasks/active/wf-f4fa1b7b-20260728-req-049/01-requirement-validation.md
//   describe the request as `{source, fieldPath, context, locale}`;
//   the 02-impact-analysis.md flagged this as a load-bearing
//   discrepancy and the TestDesigner reconciled AC-3 to the
//   actual FR-VAL-4 shape. The `fieldPath` prop is still consumed
//   by the hook for cache-key grouping + as a `data-field-path`
//   attribute on the underlying textarea (a future FR-VAL-4
//   extension that adds per-rule line/column data would map
//   server-side errors to it; in v1 the SPA just renders the
//   single `error` string verbatim via InlineFieldError).
//
// INVARIANTS:
//   - INV-2: visibility rules remain cosmetic UX. The hook returns
//     the server's verdict only; the SPA does not act on it as a
//     security boundary. The `ok: true` result is rendered as
//     informational chrome only.
//   - INV-5: no secret values are placed in the request body. The
//     `context` is treated as an evaluation Env for expr-lang
//     variable resolution (e.g. { Amount: 100, Currency: "USD" })
//     — never API keys, JWTs, or other credentials.
//   - INV-9: no `dangerouslySetInnerHTML`. The returned `error`
//     string is rendered as a React text node via
//     `<InlineFieldError>`. The hook itself never touches the DOM.
//   - FR-UI-NFR-2 (no client-side eval): the hook makes one
//     `fetch` call per debounced validate. It never imports
//     expr-lang/expr, expr-eval, wazero, fengari, wasmoon,
//     `eval(`, or `new Function(`. The existing
//     apps/web/src/runtime/staticGuards.test.ts AC-1c/AC-2b/AC-3e
//     guard is structural.
//
// DEBOUNCE / CANCELLATION:
//   - `debounceMs` (default 300): typing bursts collapse into a
//     single fetch. AC-2 requires "no network per keystroke".
//   - The hook manages an internal `AbortController`; when a newer
//     request supersedes an in-flight one, the older controller's
//     `abort()` is invoked. Only the most recent response updates
//     component state (AC-4).
//   - `validate(value, opts?)` is the imperative trigger used by
//     the explicit "Validate" button click and on-blur events.

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/api";

/** The six UI states the FormulaEditor surfaces (AC-5). Mirrors
 *  the platform-wide 5-state pattern from useDefinition +
 *  useFieldEvaluations + the sixth "successful-validated" state. */
export type FormulaValidateStatus =
  | "idle"
  | "loading"
  | "success"
  | "fetch-failure"
  | "permission-denied"
  | "stale-version"
  | "rate-limit";

/** Successful validate response from the server. */
export interface FormulaValidateSuccess {
  ok: true;
  type: string;
}

/** Failed validate response from the server (200 OK, but ok:false). */
export interface FormulaValidateError {
  ok: false;
  error: string;
  code?: string;
}

/** Union of the two server-valid responses. */
export type FormulaValidateResult =
  FormulaValidateSuccess | FormulaValidateError;

/** What the hook returns to the consumer. */
export interface UseFormulaValidateResult {
  status: FormulaValidateStatus;
  /** The latest successful or error result. `null` until the first
   *  response settles. */
  result: FormulaValidateResult | null;
  /** The most recent request error (network failure, abort, 5xx).
   *  `null` when no error has occurred. */
  error: Error | null;
  /** Seconds until the next attempt is allowed (429 backpressure). */
  retryAfterSeconds: number | null;
  /** Imperatively trigger a validate of the supplied formula. Cancels
   *  any in-flight request before issuing the new one. */
  validate: (formula: string, opts?: { immediate?: boolean }) => void;
  /** Cancel any in-flight request. Used by component unmount + by
   *  the explicit "Cancel" affordance. */
  cancel: () => void;
}

/** Wire shape sent on POST /api/formula/validate. */
interface ValidateRequestBody {
  formula: string;
  context?: Record<string, unknown>;
}

export interface UseFormulaValidateOptions {
  /** Debounce window in ms for the auto-validate path. Default 300.
   *  Set to 0 to disable debouncing (each `validate()` call fires
   *  immediately — useful for the imperative Validate button). */
  debounceMs?: number;
  /** Evaluation context passed to the validator (FR-VAL-4 contract
   *  allows an optional `context` map). Treated as opaque data;
   *  the SPA never inspects its contents. */
  context?: Record<string, unknown>;
  /** API base URL override. Defaults to `/api`. Tests point MSW at
   *  a controlled origin via this prop. */
  apiBase?: string;
}

/**
 * useFormulaValidate — debounced + AbortController-wrapped hook for
 * the server-side formula validator (FR-VAL-4 / FR-UI-ADM-3).
 */
export function useFormulaValidate(
  opts: UseFormulaValidateOptions = {},
): UseFormulaValidateResult {
  const { debounceMs = 300, context, apiBase = "/api" } = opts;

  const [status, setStatus] = useState<FormulaValidateStatus>("idle");
  const [result, setResult] = useState<FormulaValidateResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(
    null,
  );

  // The active AbortController. Replaced on every new request; the
  // previous one is aborted before its replacement fires so stale
  // responses never overwrite fresh state (AC-4).
  const inflightRef = useRef<AbortController | null>(null);
  // Latest formula to validate. Stored in a ref so the debounce
  // timeout callback can read the current value without forcing a
  // re-render on every keystroke.
  const pendingFormulaRef = useRef<string | null>(null);
  // The pending debounce timer id. Cleared on every new keystroke.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback((): void => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (inflightRef.current !== null) {
      inflightRef.current.abort();
      inflightRef.current = null;
    }
    // Status returns to idle; we do not clear result/error so the
    // most recent settle remains visible until the next validate
    // supersedes it.
  }, []);

  const fireValidate = useCallback(
    async (formula: string, controller: AbortController): Promise<void> => {
      setStatus("loading");
      setError(null);
      setRetryAfterSeconds(null);
      try {
        const body: ValidateRequestBody = { formula };
        if (context !== undefined) {
          body.context = context;
        }
        // ISS-377 round 2 (B1.2): this call site was the canonical
        // raw-fetch bypass. The round-1 regex did not match the
        // `fetch(`${apiBase}/formula/validate`, ...)` shape (the
        // template literal with `${apiBase}` interpolation slipped
        // past `['"\`]`'s character class). Migrating to apiFetch
        // attaches the OIDC bearer via the auto-bearer accessor and
        // routes any 401 through the silent-refresh path. The
        // AbortSignal is forwarded via `init.signal` so the
        // debounce/cancel semantics are unchanged.
        const response = await apiFetch(`${apiBase}/formula/validate`, {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            // SECURITY: the bearer is attached by apiFetch via
            // the auto-bearer accessor; never put credentials in
            // the body or query string.
            body: JSON.stringify(body),
            signal: controller.signal,
          },
        });

        if (controller.signal.aborted) return;

        if (response.ok) {
          const data = (await response.json()) as {
            ok?: boolean;
            type?: string;
            error?: string;
            code?: string;
          };
          if (controller.signal.aborted) return;
          if (data.ok === true) {
            setResult({ ok: true, type: data.type ?? "unknown" });
            setStatus("success");
          } else {
            setResult({
              ok: false,
              error: data.error ?? "Compile error",
              ...(data.code !== undefined ? { code: data.code } : {}),
            });
            // 200 + ok:false is a compile/type-check failure — it is
            // NOT an HTTP error. We surface it as a success-typed
            // result (so the editor can render InlineFieldError) but
            // do NOT enter a fetch-failure status. The component
            // distinguishes by inspecting `result.ok === false`.
            setStatus("success");
          }
          return;
        }

        // Non-2xx response. Map to the 5-state error surface.
        // FR-UI-NFR-2 follow-on (defensive typing): parse the
        // error body as an explicit shape rather than relying on
        // `typeof parsedBody` inference — the inferred type of
        // `parsedBody` after `let x: T | null = null` followed by
        // `x = ... as typeof x` is `null` at the first use site
        // when TS narrows by control-flow rather than by the
        // declared type.
        type ErrorBody = { error?: string; code?: string };
        let parsedBody: ErrorBody | null = null;
        try {
          const raw: unknown = await response.json();
          if (raw !== null && typeof raw === "object") {
            const candidate = raw as ErrorBody;
            parsedBody =
              typeof candidate.error === "string" ||
              typeof candidate.code === "string"
                ? candidate
                : null;
          }
        } catch {
          parsedBody = null;
        }
        if (controller.signal.aborted) return;
        const message =
          parsedBody?.error ?? `HTTP ${response.status} ${response.statusText}`;
        const next: Error = new Error(message);
        if (response.status === 401 || response.status === 403) {
          setStatus("permission-denied");
        } else if (response.status === 429) {
          setStatus("rate-limit");
          const retryAfter = response.headers.get("Retry-After");
          const seconds =
            retryAfter !== null ? Number.parseInt(retryAfter, 10) : NaN;
          setRetryAfterSeconds(Number.isFinite(seconds) ? seconds : null);
        } else if (response.status === 404 || response.status === 410) {
          setStatus("stale-version");
        } else {
          setStatus("fetch-failure");
        }
        setError(next);
      } catch (err) {
        if (controller.signal.aborted) return;
        // AbortError is the expected outcome of a superseded request;
        // swallow it silently so the request that triggered the abort
        // does not appear as a failure.
        if (err instanceof DOMException && err.name === "AbortError") return;
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStatus("fetch-failure");
      }
    },
    [apiBase, context],
  );

  const validate = useCallback(
    (formula: string, options?: { immediate?: boolean }): void => {
      // 1. Cancel any in-flight request (AC-4).
      if (inflightRef.current !== null) {
        inflightRef.current.abort();
        inflightRef.current = null;
      }
      // 2. Cancel any pending debounce timer.
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingFormulaRef.current = formula;
      const immediate = options?.immediate === true || debounceMs === 0;
      if (immediate) {
        const controller = new AbortController();
        inflightRef.current = controller;
        void fireValidate(formula, controller);
        return;
      }
      // 3. Schedule the debounced fire.
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        const controller = new AbortController();
        inflightRef.current = controller;
        void fireValidate(formula, controller);
      }, debounceMs);
    },
    [debounceMs, fireValidate],
  );

  // Cleanup on unmount (AC-4 — never leak an in-flight request after
  // the component that owns it is gone).
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      if (inflightRef.current !== null) {
        inflightRef.current.abort();
      }
    };
  }, []);

  return {
    status,
    result,
    error,
    retryAfterSeconds,
    validate,
    cancel,
  };
}
